import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { isEqual } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Container } from 'reactstrap'
import { PaymentElement } from '@stripe/react-stripe-js'
import PaymentMethodSelection from './PaymentMethodSelection'
import LoadingSpinner from '../../../common/loader/legacy-loaders/LoadingSpinner'
import usePaymentSession from '../../../../hooks/usePaymentSession'
import { quotePayment } from '../../../../actions/programRegistrationActions'
import { selectConsumerPlan, selectQuote, setBillingAddress } from '../../../../reducers/programRegistrationReducer'
import FieldError from '../../../common/FieldError'
import { chargedAmount, displayTotals, displayWithCurrency, totalsByCurrency } from '../../../../utility/currency'
import { debounce } from '../../../../utility/debounceHelper'

import './CheckoutStep.scss'

const BreakdownBox = ({ chargedToday, icon, title, note, amount }) => (
  <div className={chargedToday ? 'breakdown-box charged-today' : 'breakdown-box'}>
    <div className='breakdown-info'>
      <i className={icon} aria-hidden='true' />
      <div>
        <span className='breakdown-title'>{title}</span>
        <span className='breakdown-note'>{note}</span>
      </div>
    </div>
    <span className='breakdown-amount'>{amount}</span>
  </div>
)

const PaymentBreakdown = ({ usesPaymentSession }) => {
  const { t } = useTranslation()
  const course = useSelector(state => state.programPresentation.course)
  const { selectedPackage, selectedPaymentMethod } = useSelector(state => state.programRegistration)
  const quote = useSelector(selectQuote)

  if (!selectedPaymentMethod) {
    return null
  }

  const label = <h5 className='step-label'>{t('programs.registration.checkoutStep.paymentBreakdown')}</h5>

  const feeBox = (isOnline, amount) => (
    <BreakdownBox
      chargedToday={isOnline}
      icon={isOnline ? 'fa fa-credit-card' : 'fa fa-university'}
      title={isOnline ? t('programs.registration.checkoutStep.chargedToday') : t('programs.registration.checkoutStep.dueAtClub')}
      note={isOnline
        ? t('programs.landing.actionsCard.programCoachingFee')
        : t('programs.registration.checkoutStep.dueAtClubNote')}
      amount={amount}
    />
  )

  if (!usesPaymentSession) {
    return (
      <div className='payment-breakdown'>
        {label}
        {feeBox(selectedPaymentMethod === 'card', displayWithCurrency(selectedPackage.price, course.community?.currency))}
      </div>
    )
  }

  if (quote?.status !== 'ready') {
    return (
      <div className='payment-breakdown'>
        {label}
        <LoadingSpinner
          text={t('programs.registration.checkoutStep.loadingQuote')}
          classNames={{ spinner: 'fa-2x', text: 'loading-quote-text' }}
        />
      </div>
    )
  }

  const feeLine = quote.pricing.lines.find(line => line.key === 'program_fee')
  const membershipLine = quote.pricing.lines.find(line => line.key === 'membership')
  const onlineFee = feeLine?.paymentMethod === 'card'

  return (
    <div className='payment-breakdown'>
      {label}
      {onlineFee && !!membershipLine
        ? (
          <BreakdownBox
            chargedToday
            icon='fa fa-credit-card'
            title={t('programs.registration.checkoutStep.chargedToday')}
            note={t('programs.registration.checkoutStep.coachingFeeAndMembership')}
            amount={displayTotals(totalsByCurrency([feeLine, membershipLine]))}
          />
        )
        : (
          <>
            {!!membershipLine && (
              <BreakdownBox
                chargedToday
                icon='fa fa-id-card'
                title={t('programs.registration.checkoutStep.membershipChargedToday')}
                note={t('programs.registration.checkoutStep.membershipNote', {
                  tier: t(`programs.registration.membershipPlan.tiers.${membershipLine.plan.tier}`),
                })}
                amount={displayWithCurrency(chargedAmount(membershipLine), membershipLine.currency)}
              />
            )}
            {!!feeLine && feeBox(onlineFee, displayWithCurrency(chargedAmount(feeLine), feeLine.currency))}
          </>
        )}
      {quote.membership?.covered && (
        <p className='membership-included-note'>{t('programs.registration.checkoutStep.membershipIncluded')}</p>
      )}
    </div>
  )
}

const PaymentDetails = ({ usesPaymentSession }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentUser = useSelector(state => state.user.current_user)
  const course = useSelector(state => state.programPresentation.course)
  const paymentSession = useSelector(state => state.programRegistration.paymentSession)
  const billingAddressError = useSelector(state => state.programRegistration.billingAddressError)
  const quote = useSelector(selectQuote)

  const requote = useMemo(
    () => debounce((registrationId, params) => dispatch(quotePayment({ registrationId, ...params }))),
    [dispatch]
  )

  const handleCardChange = (event) => {
    const address = event.value.billingDetails?.address

    if (!address?.country || address.postalCode === '' || isEqual(address, paymentSession.billingAddress)) {
      return
    }

    dispatch(setBillingAddress({ ...address }))
    requote(paymentSession.registrationId, quote.params)
  }

  if (!usesPaymentSession) {
    return null
  }

  const sessionReady = paymentSession?.status === 'ready'

  // gated on a quote ever having arrived, not on the current one: a re-quote must not unmount the card form
  if (!sessionReady || !quote?.pricing) {
    return (
      <div className='payment-details'>
        <LoadingSpinner
          text={sessionReady
            ? t('programs.registration.checkoutStep.loadingQuote')
            : t('programs.registration.checkoutStep.loadingPaymentForm')}
          classNames={{ spinner: 'fa-2x', text: 'loading-payment-form-text' }}
        />
      </div>
    )
  }

  if (!quote.cardRequired) {
    return null
  }

  const twoConfirmations = quote.pricing.lines.filter(line => line.paymentMethod === 'card').length > 1

  return (
    <div className='payment-details'>
      <h5 className='step-label'>{t('programs.registration.checkoutStep.paymentDetails')}</h5>
      <label className='payer-email-label' htmlFor='payer-email'>{t('common.field.email')}</label>
      <input id='payer-email' type='email' className='payer-email' value={currentUser?.email || ''} readOnly />
      <PaymentElement
        options={{
          wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
          fields: { billingDetails: { email: 'never' } },
          terms: { card: 'never' },
        }}
        onChange={handleCardChange}
      />
      {billingAddressError && <FieldError message={billingAddressError} />}
      {twoConfirmations && <p className='two-charges-note'>{t('programs.registration.checkoutStep.twoChargesNote')}</p>}
      <p className='card-terms'>{t('programs.registration.checkoutStep.cardTermsTooltip', { communityName: course.community?.name })}</p>
      <p className='secure-payments-note'>
        <i className='fa fa-lock' aria-hidden='true' /> {t('programs.registration.checkoutStep.securePayments')}
      </p>
    </div>
  )
}

const CheckoutStep = () => {
  const { t } = useTranslation()
  const { coursePackages } = useSelector(state => state.programRegistration.enrollmentData)
  const selectedPaymentMethod = useSelector(state => state.programRegistration.selectedPaymentMethod)
  const consumerPlan = useSelector(selectConsumerPlan)

  usePaymentSession()

  const withPackages = coursePackages.length > 0
  const usesPaymentSession = consumerPlan || selectedPaymentMethod === 'card'

  return (
    <Container fluid id='CheckoutStep'>
      <h2 className='d-none d-lg-block step-title'>{t('programs.registration.checkoutStep.title')}</h2>
      {(withPackages || consumerPlan) && (
        <>
          {withPackages && <PaymentMethodSelection />}
          <PaymentBreakdown usesPaymentSession={usesPaymentSession} />
          <PaymentDetails usesPaymentSession={usesPaymentSession} />
        </>
      )}
    </Container>
  )
}

BreakdownBox.propTypes = {
  chargedToday: PropTypes.bool,
  icon: PropTypes.string,
  title: PropTypes.string,
  note: PropTypes.string,
  amount: PropTypes.string,
}

PaymentBreakdown.propTypes = {
  usesPaymentSession: PropTypes.bool,
}

PaymentDetails.propTypes = {
  usesPaymentSession: PropTypes.bool,
}

export default CheckoutStep
